import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Stage, Layer, Image as KonvaImage, Group, Rect, Line, Text } from 'react-konva';
import type Konva from 'konva';
import {
  centerNudge,
  clampInFrame,
  coverTransform,
  exportMime,
  exportQuality,
  fitFontSize,
  useHtmlImage,
  type ExportFormat,
  type PhotoTransform,
} from './photoSlot';
import { COLORS } from './templates';
import { TAKEOVER_FILL, type TakeoverFill, type TakeoverTemplate, type TakeoverText } from './takeovers';

const LINE_HEIGHT = 1.05;

export interface TakeoverValue {
  photoSrc: string | null;
  transform: PhotoTransform | null;
  name: string;
  date?: string;
  time?: string;
}

export interface TakeoverEditorHandle {
  toDataURL: (format?: ExportFormat) => string | null;
  toBlob: (format?: ExportFormat) => Promise<Blob | null>;
}

interface TakeoverEditorProps {
  template: TakeoverTemplate;
  value: TakeoverValue;
  fill: TakeoverFill;
  onChange: (next: TakeoverValue) => void;
  className?: string;
}

export const TakeoverEditor = forwardRef<TakeoverEditorHandle, TakeoverEditorProps>(function TakeoverEditor(
  { template, value, fill, onChange, className },
  ref,
) {
  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const photoRef = useRef<Konva.Image>(null);
  const { canvas } = template;

  const photoImg = useHtmlImage(value.photoSrc);
  const logoImg = useHtmlImage(fill === 'black' ? '/card-editor/takeover/logo-black.png' : '/card-editor/takeover/logo-navy.png');
  const [fontReady, setFontReady] = useState(false);
  const [fit, setFit] = useState(0.3);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const availW = el.clientWidth;
      const availH = Math.min(window.innerHeight * 0.72, 1100);
      setFit(Math.min(availW / canvas.width, availH / canvas.height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [canvas.width, canvas.height]);

  useEffect(() => {
    let active = true;
    const done = () => active && setFontReady(true);
    if (typeof document !== 'undefined' && 'fonts' in document) {
      Promise.all([
        document.fonts.load('400 40px "Display Robust"'),
        document.fonts.load('700 40px "Space Mono"'),
      ])
        .then(() => document.fonts.ready)
        .then(done)
        .catch(done);
    } else {
      done();
    }
    return () => {
      active = false;
    };
  }, []);

  const frame = template.photoFrame;
  useEffect(() => {
    if (photoImg && !value.transform) {
      onChange({ ...value, transform: coverTransform(photoImg.width, photoImg.height, frame) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoImg, frame.width, frame.height]);

  const renderAt2x = useCallback(
    <T,>(draw: (stage: Konva.Stage) => T): T | null => {
      const stage = stageRef.current;
      if (!stage) return null;
      stage.scale({ x: 1, y: 1 });
      stage.size({ width: canvas.width, height: canvas.height });
      const out = draw(stage);
      stage.scale({ x: fit, y: fit });
      stage.size({ width: canvas.width * fit, height: canvas.height * fit });
      return out;
    },
    [fit, canvas.width, canvas.height],
  );

  useImperativeHandle(
    ref,
    () => ({
      toDataURL: (format: ExportFormat = 'png') =>
        renderAt2x((s) => s.toDataURL({ pixelRatio: 2, mimeType: exportMime(format), quality: exportQuality(format) })),
      toBlob: (format: ExportFormat = 'png') =>
        new Promise<Blob | null>((resolve) => {
          const done = renderAt2x((s) =>
            s.toCanvas({ pixelRatio: 2 }).toBlob((b: Blob | null) => resolve(b), exportMime(format), exportQuality(format)),
          );
          if (done === null) resolve(null);
        }),
    }),
    [renderAt2x],
  );

  const logoDraw = useMemo(() => {
    const r = template.logoRect;
    if (!logoImg) return r;
    const s = Math.min(r.width / logoImg.width, r.height / logoImg.height);
    return { x: r.x, y: r.y, width: logoImg.width * s, height: logoImg.height * s };
  }, [logoImg, template.logoRect]);

  const panelFill = TAKEOVER_FILL[fill];

  const panel = (tb: TakeoverText, text: string, keySeed: string) => {
    const ctx = document.createElement('canvas').getContext('2d');
    const size = ctx
      ? fitFontSize(ctx, text || ' ', tb.box, tb.fontSize, tb.fontFamily, { lineHeight: LINE_HEIGHT, weight: tb.weight })
      : tb.fontSize;
    const nudge = ctx ? centerNudge(ctx, text || 'X', size, tb.fontFamily, tb.weight) : 0;
    return (
      <Group key={keySeed}>
        {tb.connector && (
          <Line
            points={[tb.connector.x, tb.connector.y, tb.connector.x, tb.connector.y + tb.connector.length]}
            stroke={COLORS.orange}
            strokeWidth={2}
            listening={false}
          />
        )}
        <Rect
          x={tb.box.x}
          y={tb.box.y}
          width={tb.box.width}
          height={tb.box.height}
          fill={panelFill}
          stroke={COLORS.orange}
          strokeWidth={2}
          listening={false}
        />
        <Text
          key={fontReady ? `${keySeed}-r` : `${keySeed}-l`}
          x={tb.box.x}
          y={tb.box.y + nudge}
          width={tb.box.width}
          height={tb.box.height}
          text={text}
          fontFamily={tb.fontFamily}
          fontSize={size}
          fontStyle={tb.weight >= 700 ? 'bold' : 'normal'}
          fill={COLORS.orange}
          align={tb.align}
          verticalAlign="middle"
          lineHeight={LINE_HEIGHT}
          wrap="word"
          listening={false}
        />
      </Group>
    );
  };

  return (
    <div ref={containerRef} className={className}>
      <Stage
        ref={stageRef}
        width={canvas.width * fit}
        height={canvas.height * fit}
        scaleX={fit}
        scaleY={fit}
        style={{ borderRadius: 8, overflow: 'hidden' }}
      >
        <Layer>
          {/* Base behind the full-bleed photo — the toggle colour, so a smaller
              or transparent image sits on navy/black rather than a hole. */}
          <Rect x={0} y={0} width={canvas.width} height={canvas.height} fill={panelFill} listening={false} />

          {/* Full-bleed photo */}
          <Group clipX={0} clipY={0} clipWidth={canvas.width} clipHeight={canvas.height}>
            {photoImg && value.transform ? (
              <KonvaImage
                ref={photoRef}
                image={photoImg}
                x={value.transform.x}
                y={value.transform.y}
                scaleX={value.transform.scale}
                scaleY={value.transform.scale}
                draggable
                onDragEnd={(e) => {
                  const c = clampInFrame(e.target.x(), e.target.y(), photoImg.width, photoImg.height, value.transform!.scale, frame);
                  onChange({ ...value, transform: { scale: value.transform!.scale, ...c } });
                }}
                dragBoundFunc={(pos) => {
                  if (!value.transform) return pos;
                  const s = photoRef.current?.getStage()?.scaleX() ?? 1;
                  const local = clampInFrame(pos.x / s, pos.y / s, photoImg.width, photoImg.height, value.transform.scale, frame);
                  return { x: local.x * s, y: local.y * s };
                }}
              />
            ) : (
              <Text
                x={0}
                y={canvas.height / 2 - 24}
                width={canvas.width}
                text="Drop a full-bleed photo"
                fontFamily="sans-serif"
                fontSize={40}
                fill="#ffffff"
                opacity={0.4}
                align="center"
                listening={false}
              />
            )}
          </Group>

          {/* Decorative orange frame over the photo (INSIDE stroke) */}
          <Rect
            x={template.border.rect.x + template.border.width / 2}
            y={template.border.rect.y + template.border.width / 2}
            width={template.border.rect.width - template.border.width}
            height={template.border.rect.height - template.border.width}
            stroke={COLORS.orange}
            strokeWidth={template.border.width}
            listening={false}
          />

          {/* Branding panels */}
          {template.takeoverLabel && panel(template.takeoverLabel, template.takeoverLabel.text, 'label')}
          {panel(template.name, value.name || 'artist name', 'name')}
          {template.date && panel(template.date, value.date || 'DATE', 'date')}
          {template.time && panel(template.time, value.time || 'time', 'time')}

          {/* Logo */}
          {logoImg && (
            <KonvaImage
              image={logoImg}
              x={logoDraw.x}
              y={logoDraw.y}
              width={logoDraw.width}
              height={logoDraw.height}
              listening={false}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
});
